import { createContext, useContext, useState, ReactNode } from 'react';

/**
 * `underline` es el estilo historico (subrayado primario) y sigue siendo el
 * default: no cambia ninguna pantalla que ya usaba Tabs. `pill` es el de
 * Claude Design (Configuracion.dc.html): capsula blanca con la pestaña activa
 * en navy.
 */
type TabsVariant = 'underline' | 'pill';

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (value: string) => void;
  variant: TabsVariant;
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children: ReactNode;
  className?: string;
  variant?: TabsVariant;
}

export function Tabs({
  defaultValue,
  value,
  onValueChange,
  children,
  className = '',
  variant = 'underline',
}: TabsProps) {
  const [internalTab, setInternalTab] = useState(defaultValue || value || '');

  // Use controlled value if provided, otherwise use internal state
  const activeTab = value !== undefined ? value : internalTab;

  const handleTabChange = (newTab: string) => {
    if (value === undefined) {
      // Uncontrolled mode
      setInternalTab(newTab);
    }
    // Always call onValueChange if provided
    onValueChange?.(newTab);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleTabChange, variant }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function TabsList({ children, className = '', 'data-testid': testId }: TabsListProps) {
  const variant = useContext(TabsContext)?.variant ?? 'underline';

  if (variant === 'pill') {
    // Scroll horizontal propio: con 3 pestañas largas la capsula no cabe en
    // pantallas angostas, y romperla en dos lineas la desarma.
    return (
      <div className={`overflow-x-auto pb-0.5 ${className}`}>
        <div
          role="tablist"
          data-testid={testId}
          className="inline-flex max-w-max gap-1 rounded-xl border border-[#ececec] bg-white p-[5px] shadow-[0_1px_2px_rgba(10,34,51,.03)] dark:border-secondary-700 dark:bg-secondary-800"
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className={`border-b border-secondary-200 dark:border-secondary-700 ${className}`}
      role="tablist"
    >
      <div className="flex space-x-8">{children}</div>
    </div>
  );
}

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
  /** Icono a la izquierda del texto. Solo lo usa la variante `pill`. */
  icon?: ReactNode;
  'data-testid'?: string;
}

export function TabsTrigger({ value, children, className = '', icon, 'data-testid': testId }: TabsTriggerProps) {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('TabsTrigger must be used within Tabs');
  }

  const { activeTab, setActiveTab, variant } = context;
  const isActive = activeTab === value;

  if (variant === 'pill') {
    return (
      <button
        type="button"
        role="tab"
        aria-selected={isActive}
        data-testid={testId}
        onClick={() => setActiveTab(value)}
        className={`
          inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-4 py-[9px] text-[14px] font-semibold transition-colors
          ${
            isActive
              ? 'bg-ink-850 text-white'
              : 'text-secondary-500 hover:text-ink-850 dark:text-secondary-400 dark:hover:text-white'
          }
          ${className}
        `}
      >
        {icon}
        {children}
      </button>
    );
  }

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      data-testid={testId}
      onClick={() => setActiveTab(value)}
      className={`
        pb-4 px-1 border-b-2 font-medium text-sm transition-colors
        ${
          isActive
            ? 'border-primary-600 text-primary-600 dark:text-primary-500'
            : 'border-transparent text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-300'
        }
        ${className}
      `}
    >
      {children}
    </button>
  );
}

/**
 * Contador / etiqueta dentro de una pestaña `pill` (el `.badge` del diseño).
 * `tone="warn"` es el amarillo que el diseño usa para "Pendiente".
 */
export function TabsBadge({
  children,
  tone = 'default',
  active = false,
}: {
  children: ReactNode;
  tone?: 'default' | 'warn';
  active?: boolean;
}) {
  const tones = {
    default: active ? 'bg-white/[.15] text-white' : 'bg-[#f4f4f4] text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300',
    warn: active ? 'bg-highlight-400 text-ink-850' : 'bg-highlight-400/[.24] text-[#7a5a06]',
  };

  return (
    <span className={`rounded-full px-[7px] py-px font-mono text-[11px] font-bold ${tones[tone]}`}>
      {children}
    </span>
  );
}

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function TabsContent({ value, children, className = '', 'data-testid': testId }: TabsContentProps) {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('TabsContent must be used within Tabs');
  }

  const { activeTab, variant } = context;

  if (activeTab !== value) {
    return null;
  }

  return (
    <div data-testid={testId} role="tabpanel" className={`${variant === 'pill' ? '' : 'mt-6'} ${className}`}>
      {children}
    </div>
  );
}
